import { Component, computed, inject, OnDestroy, signal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';
import { LoginUsersClient, ResetPasswordCommand } from '../../shared/model/fss.api';
import { LoaderService } from '../../shared/components/loader/loader.service';
import { AlertService } from '../../shared/services/alert.service';
import { AuthService } from '../../shared/services/auth.service';
import { LocationService } from '../../shared/services/location.service';
import { FieldComponent } from '../../shared/ui/field/field';
import { TextInput } from '../../shared/ui/text-input/text-input';
import { FormControlPipe } from '../../shared/pipes/form-control.pipe';
import { ButtonComponent, ButtonSize } from '../../shared/ui/button/button';
import { Lock03 } from '../../shared/icons/lock-03/lock-03';
import { CheckCircle } from '../../shared/icons/check-circle/check-circle';
import {
  PASSWORD_MIN_LENGTH,
  PasswordRequirements,
} from '../../shared/components/password-requirements/password-requirements';

@Component({
  standalone: true,
  selector: 'app-reset-password',
  imports: [
    ReactiveFormsModule,
    FieldComponent,
    TextInput,
    FormControlPipe,
    ButtonComponent,
    Lock03,
    CheckCircle,
    PasswordRequirements,
  ],
  templateUrl: './reset-password.html',
  styleUrl: './reset-password.scss',
})
export class ResetPassword implements OnDestroy {
  private readonly _fb = inject(FormBuilder);
  private readonly _client = inject(LoginUsersClient);
  private readonly _loader = inject(LoaderService);
  private readonly _alert = inject(AlertService);
  private readonly _auth = inject(AuthService);
  private readonly _location = inject(LocationService);

  currentYear = new Date().getFullYear();
  isSuccess = signal(false);
  private _handledExit = false;

  form: FormGroup = this._fb.group({
    password: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(PASSWORD_MIN_LENGTH)],
    }),
    confirmPassword: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
  });

  protected readonly passwordValue = toSignal(this.form.controls['password'].valueChanges, {
    initialValue: '',
  });

  hasSpecialChar = computed(() => /[^A-Za-z0-9]/.test(this.passwordValue() ?? ''));

  submit() {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    const { password, confirmPassword } = this.form.value;
    if (password !== confirmPassword) {
      this._alert.addWarnMsg('Lozinke se ne podudaraju');
      return;
    }
    if (!this.hasSpecialChar()) {
      this._alert.addWarnMsg('Lozinka mora sadržati specijalni karakter');
      return;
    }

    this._loader.setLoadingText = 'Resetovanje lozinke...';
    this._loader.setShowLoader = true;

    firstValueFrom(
      this._client.resetPassword(new ResetPasswordCommand({ password, confirmPassword })),
    ).then(() => {
      this.isSuccess.set(true);
    });
  }

  goToLogin() {
    this._handledExit = true;
    this._auth.removeTokens();
    this._location.routeToLogin();
  }

  ngOnDestroy(): void {
    if (this._handledExit) return;
    this._auth.removeTokens();
    this._location.routeToLogin();
  }

  protected readonly ButtonSize = ButtonSize;
}
